import { useEffect, useState } from "react";
import {
  getMyMemberships, getMyDonations, getMyReceipts,
  downloadMyReceiptPdf, getMyAuthorProfile, updateMyAuthorProfile,
} from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Skeleton } from "./ui/skeleton";
import { toast } from "sonner";
import {
  CreditCard, Calendar, Check, Clock, AlertCircle, Download,
  FileText, HandCoins, PenLine, Info,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const EDITORIAL_ROLES = ["editor", "admin", "super_admin"];

const membershipStatus = {
  active: { label: "Adhésion active", color: "bg-green-100 text-green-700", icon: Check },
  pending: { label: "En attente", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  expired: { label: "Expirée", color: "bg-red-100 text-red-700", icon: AlertCircle },
  suspended: { label: "Suspendue", color: "bg-gray-100 text-gray-700", icon: AlertCircle },
  cancelled: { label: "Annulée", color: "bg-gray-100 text-gray-700", icon: AlertCircle },
};

const receiptTypeLabel = (t) => {
  const v = (t || "").toUpperCase();
  if (v === "DONATION" || v === "DON") return "DON";
  if (v === "MEMBERSHIP" || v === "COTISATION") return "COTISATION";
  return v || "—";
};

function fmtDate(d) {
  if (!d) return "—";
  try {
    return format(new Date(d), "d MMM yyyy", { locale: fr });
  } catch {
    return d;
  }
}

function fmtAmount(a, c = "EUR") {
  if (a == null) return "—";
  return `${Number(a).toFixed(2)} ${c}`;
}

export default function MemberAccountSections({ user }) {
  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState([]);
  const [donations, setDonations] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  const isEditorial = EDITORIAL_ROLES.includes(user?.role);

  useEffect(() => {
    (async () => {
      try {
        const [m, d, r] = await Promise.all([
          getMyMemberships().catch(() => ({ data: { memberships: [] } })),
          getMyDonations().catch(() => ({ data: { donations: [] } })),
          getMyReceipts().catch(() => ({ data: { receipts: [] } })),
        ]);
        setMemberships(m.data?.memberships || []);
        setDonations(d.data?.donations || []);
        setReceipts(r.data?.receipts || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDownload = async (receipt) => {
    setDownloadingId(receipt.id);
    try {
      const res = await downloadMyReceiptPdf(receipt.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `recu-${receipt.receipt_number || receipt.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossible de télécharger le reçu pour le moment.");
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="member-account-sections-loading">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="member-account-sections">
      {/* Adhésion */}
      <section className="bg-white border border-border rounded-lg p-6" data-testid="membership-detail-card">
        <h2 className="font-heading text-lg font-semibold text-primary mb-5 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-accent" /> Mon adhésion
        </h2>
        {memberships.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune adhésion enregistrée pour le moment.</p>
        ) : (
          <div className="space-y-5">
            {memberships.map((m) => {
              const cfg = membershipStatus[m.status] || membershipStatus.pending;
              const StatusIcon = cfg.icon;
              return (
                <div key={m.id} className="border border-border rounded-lg p-4" data-testid={`membership-row-${m.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${cfg.color}`} data-testid={`membership-status-${m.id}`}>
                      <StatusIcon className="h-4 w-4" /> {cfg.label}
                    </span>
                    {m.member_id && (
                      <span className="text-xs text-muted-foreground">N° adhérent : {m.member_id}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>Début : {fmtDate(m.start_date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>Échéance : {fmtDate(m.end_date)}</span>
                    </div>
                    {m.amount != null && (
                      <div className="flex items-center gap-2">
                        <HandCoins className="h-4 w-4" />
                        <span>Cotisation : {fmtAmount(m.amount, m.currency)}</span>
                      </div>
                    )}
                  </div>
                  {m.status === "active" && (
                    <p className="mt-3 text-xs text-muted-foreground bg-muted rounded-md px-3 py-2 flex items-start gap-2">
                      <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      Votre adhésion est active. Le reçu fiscal correspondant apparaîtra dans « Mes reçus fiscaux »
                      dès son émission — il peut être affiché <strong>en attente d'émission</strong> quelques jours.
                    </p>
                  )}
                  {m.status === "expired" && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Votre adhésion a expiré. Renouvelez-la pour continuer à profiter des avantages.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Reçus fiscaux */}
      <section className="bg-white border border-border rounded-lg p-6" data-testid="tax-receipts-card">
        <h2 className="font-heading text-lg font-semibold text-primary mb-5 flex items-center gap-2">
          <FileText className="h-5 w-5 text-accent" /> Mes reçus fiscaux
        </h2>
        {receipts.length === 0 ? (
          <div className="text-sm text-muted-foreground bg-muted rounded-md px-3 py-3 flex items-start gap-2" data-testid="receipts-empty">
            <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />
            Aucun reçu fiscal émis pour l'instant. Si vous avez une adhésion ou un don récent,
            votre reçu est <strong>en attente d'émission</strong> et apparaîtra ici automatiquement.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="receipts-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3 font-medium">Numéro</th>
                  <th className="py-2 pr-3 font-medium">Date</th>
                  <th className="py-2 pr-3 font-medium">Type</th>
                  <th className="py-2 pr-3 font-medium">Montant</th>
                  <th className="py-2 pr-3 font-medium">Statut</th>
                  <th className="py-2 font-medium text-right">Reçu</th>
                </tr>
              </thead>
              <tbody>
                {receipts.map((r) => (
                  <tr key={r.id} className="border-b border-border/60" data-testid={`receipt-row-${r.id}`}>
                    <td className="py-2.5 pr-3 font-mono text-xs">{r.receipt_number || "—"}</td>
                    <td className="py-2.5 pr-3">{fmtDate(r.issue_date || r.payment_date)}</td>
                    <td className="py-2.5 pr-3">{receiptTypeLabel(r.transaction_type)}</td>
                    <td className="py-2.5 pr-3">{fmtAmount(r.amount, r.currency)}</td>
                    <td className="py-2.5 pr-3">
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        {r.status || "émis"}
                      </span>
                    </td>
                    <td className="py-2.5 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(r)}
                        disabled={downloadingId === r.id}
                        data-testid={`receipt-download-${r.id}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        {downloadingId === r.id ? "…" : "Télécharger"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Historique donations */}
      <section className="bg-white border border-border rounded-lg p-6" data-testid="donations-card">
        <h2 className="font-heading text-lg font-semibold text-primary mb-5 flex items-center gap-2">
          <HandCoins className="h-5 w-5 text-accent" /> Historique de mes dons
        </h2>
        {donations.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="donations-empty">
            Vous n'avez effectué aucun don pour le moment.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="donations-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3 font-medium">Date</th>
                  <th className="py-2 pr-3 font-medium">Montant</th>
                  <th className="py-2 font-medium">Statut</th>
                </tr>
              </thead>
              <tbody>
                {donations.map((d, i) => (
                  <tr key={d.id || i} className="border-b border-border/60" data-testid={`donation-row-${d.id || i}`}>
                    <td className="py-2.5 pr-3">{fmtDate(d.created_at || d.payment_date || d.date)}</td>
                    <td className="py-2.5 pr-3">{fmtAmount(d.amount, d.currency || "EUR")}</td>
                    <td className="py-2.5">{d.status || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Profil auteur public (rôle éditorial uniquement) */}
      {isEditorial && <AuthorProfileForm />}
    </div>
  );
}

function AuthorProfileForm() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    display_name: "", public_alias: "", public_bio: "",
    public_avatar_url: "", editorial_title: "", show_author_publicly: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await getMyAuthorProfile();
        const p = res.data || {};
        setForm({
          display_name: p.display_name || "",
          public_alias: p.public_alias || "",
          public_bio: p.public_bio || "",
          public_avatar_url: p.public_avatar_url || "",
          editorial_title: p.editorial_title || "",
          show_author_publicly: !!p.show_author_publicly,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMyAuthorProfile(form);
      toast.success("Profil auteur public enregistré.");
    } catch {
      toast.error("Échec de l'enregistrement du profil auteur.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <section className="bg-white border border-border rounded-lg p-6" data-testid="author-profile-card">
      <h2 className="font-heading text-lg font-semibold text-primary mb-2 flex items-center gap-2">
        <PenLine className="h-5 w-5 text-accent" /> Mon identité d'auteur public
      </h2>
      <p className="text-xs text-muted-foreground mb-5 bg-muted rounded-md px-3 py-2 flex items-start gap-2">
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        Ce nom public apparaîtra sur les articles et les PDF que vous signez. Rien n'est affiché
        si les champs sont vides ou si l'affichage public est désactivé.
      </p>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Nom affiché</label>
            <Input value={form.display_name} onChange={(e) => setField("display_name", e.target.value)}
              placeholder="Ex. Camille Voyage" data-testid="author-display-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Alias public</label>
            <Input value={form.public_alias} onChange={(e) => setField("public_alias", e.target.value)}
              placeholder="Ex. La Rédac ITERCATUS" data-testid="author-public-alias" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-primary mb-1">Titre éditorial</label>
          <Input value={form.editorial_title} onChange={(e) => setField("editorial_title", e.target.value)}
            placeholder="Ex. Rédacteur croisières" data-testid="author-editorial-title" />
        </div>
        <div>
          <label className="block text-sm font-medium text-primary mb-1">Avatar (URL Cloudinary)</label>
          <Input value={form.public_avatar_url} onChange={(e) => setField("public_avatar_url", e.target.value)}
            placeholder="https://res.cloudinary.com/..." data-testid="author-avatar-url" />
        </div>
        <div>
          <label className="block text-sm font-medium text-primary mb-1">Bio courte</label>
          <Textarea value={form.public_bio} onChange={(e) => setField("public_bio", e.target.value)}
            rows={3} placeholder="Quelques lignes qui apparaîtront sous vos articles."
            className="resize-none" data-testid="author-public-bio" />
        </div>
        <div className="flex items-center justify-between border border-border rounded-md px-4 py-3">
          <div>
            <p className="text-sm font-medium text-primary">Afficher publiquement ma signature</p>
            <p className="text-xs text-muted-foreground">Active l'affichage de votre identité sur les articles.</p>
          </div>
          <Switch
            checked={form.show_author_publicly}
            onCheckedChange={(v) => setField("show_author_publicly", v)}
            data-testid="author-show-publicly"
          />
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-accent hover:bg-accent/90" data-testid="author-profile-save">
          {saving ? "Enregistrement…" : "Enregistrer mon profil"}
        </Button>
      </div>
    </section>
  );
}
